import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { api } from '@/lib/api';
import {
  Badge,
  Button,
  Card,
  EmptyState,
  ErrorState,
  Field,
  Input,
  Loading,
  Modal,
  PageHeader,
  Select,
  StatCard,
  Toggle,
} from '@/components/ui';
import { formatDateTime } from '@/lib/format';

/**
 * Rules that watch the platform, and what they found.
 *
 * Nothing on this page is new information — every fact behind it has been in
 * the database for releases. What was missing was anything that pushed it at
 * a person: the stuck-call defect was found by an operator grepping
 * journalctl by hand over five days.
 *
 * The rule form is generated from `/admin/alerting/kinds` rather than written
 * here. A second copy of every threshold's name, type and default in
 * TypeScript drifts the first time somebody adds a field, and is then wrong in
 * a way no test can see.
 */

interface SpecField {
  key: string;
  label: string;
  type: 'number' | 'text';
  default: number | string;
  min?: number;
  max?: number;
  step?: number;
  hint?: string;
}

interface KindSpec {
  kind: string;
  label: string;
  blurb: string;
  fields: SpecField[];
  default_config: Record<string, unknown>;
}

interface Channel {
  kind: string;
  to?: string;
  chat_id?: string;
  url?: string;
  secret?: string;
}

interface Rule {
  id: string;
  name: string;
  kind: string;
  label: string;
  enabled: boolean;
  severity: string;
  tenant_id: string | null;
  client_name: string | null;
  campaign: string | null;
  config: Record<string, unknown>;
  channels: Channel[];
  cooldown_sec: number;
  muted_until: string | null;
  last_fired_at: string | null;
  open_alerts: number;
  fired_7d: number;
}

interface Alert {
  id: string;
  rule_name: string | null;
  kind: string;
  severity: string;
  title: string;
  detail: Record<string, unknown>;
  client_name: string | null;
  campaign: string | null;
  fired_at: string;
  resolved_at: string | null;
  acknowledged_at: string | null;
  delivery: Array<{ kind: string; ok: boolean; error?: string }>;
}

const TONE: Record<string, 'red' | 'amber' | 'blue'> = {
  critical: 'red',
  warning: 'amber',
  info: 'blue',
};

/** Seconds as something a person reads. Used for cooldowns. */
function every(seconds: number): string {
  if (seconds < 3600) return `${Math.round(seconds / 60)} min`;
  if (seconds < 86400) return `${Math.round(seconds / 3600)} h`;
  return `${Math.round(seconds / 86400)} d`;
}

export default function Alerting() {
  const qc = useQueryClient();
  const [tab, setTab] = useState<'alerts' | 'rules' | 'delivery'>('alerts');
  const [status, setStatus] = useState('open');
  const [editing, setEditing] = useState<Partial<Rule> | null>(null);

  const kinds = useQuery({
    queryKey: ['alert-kinds'],
    queryFn: () =>
      api.get<{ kinds: KindSpec[]; channels: string[]; severities: string[] }>(
        '/admin/alerting/kinds',
      ),
    staleTime: Infinity,
  });

  const rules = useQuery({
    queryKey: ['alert-rules'],
    queryFn: () => api.get<{ data: Rule[] }>('/admin/alerting/rules'),
  });

  const alerts = useQuery({
    queryKey: ['alerts', status],
    queryFn: () =>
      api.get<{
        data: Alert[];
        total: number;
        summary: Record<string, number>;
      }>('/admin/alerting/alerts', { status, days: 30, page_size: 100 }),
    refetchInterval: 30_000,
  });

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ['alerts'] });
    qc.invalidateQueries({ queryKey: ['alert-rules'] });
  };

  /**
   * Muting is a write-only field: it is sent as "how many minutes from now",
   * and read back as the timestamp the server worked out. Modelling it as
   * part of `Rule` would mean a shape that is neither what is sent nor what
   * comes back.
   */
  const save = useMutation({
    mutationFn: (rule: Partial<Rule> & { mute_minutes?: number }) =>
      rule.id
        ? api.patch(`/admin/alerting/rules/${rule.id}`, rule)
        : api.post('/admin/alerting/rules', rule),
    onSuccess: () => {
      setEditing(null);
      invalidate();
    },
  });

  const remove = useMutation({
    mutationFn: (id: string) => api.delete(`/admin/alerting/rules/${id}`),
    onSuccess: invalidate,
  });

  const ack = useMutation({
    mutationFn: (id: string) => api.post(`/admin/alerting/alerts/${id}/ack`, {}),
    onSuccess: invalidate,
  });

  const ackAll = useMutation({
    mutationFn: () => api.post('/admin/alerting/alerts/ack-all', {}),
    onSuccess: invalidate,
  });

  const runNow = useMutation({
    mutationFn: () => api.post('/admin/alerting/run', {}),
    onSuccess: invalidate,
  });

  const summary = alerts.data?.summary;

  return (
    <div className="w-full">
      <PageHeader
        title="Alerting"
        description="What the platform watches for, and what it found."
        actions={
          <div className="flex gap-2">
            <Button
              variant="secondary"
              onClick={() => runNow.mutate()}
              disabled={runNow.isPending}
            >
              {runNow.isPending ? 'Checking…' : 'Check now'}
            </Button>
            <Button
              onClick={() =>
                setEditing({
                  name: '',
                  kind: kinds.data?.kinds[0]?.kind,
                  severity: 'warning',
                  enabled: true,
                  cooldown_sec: 3600,
                  channels: [],
                  config: kinds.data?.kinds[0]?.default_config ?? {},
                })
              }
            >
              New rule
            </Button>
          </div>
        }
      />

      {summary && (
        <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
          <StatCard label="Open" value={String(summary.open ?? 0)} dense />
          <StatCard
            label="Critical"
            value={String(summary.critical ?? 0)}
            dense
            tone={summary.critical ? 'bad' : 'default'}
          />
          <StatCard
            label="Unacknowledged"
            value={String(summary.unacknowledged ?? 0)}
            dense
          />
          <StatCard label="Last 24 hours" value={String(summary.today ?? 0)} dense />
        </div>
      )}

      <div className="mt-4 flex gap-1 border-b border-line">
        {(['alerts', 'rules', 'delivery'] as const).map((item) => (
          <button
            key={item}
            onClick={() => setTab(item)}
            className={
              'px-3 py-2 text-sm capitalize ' +
              (tab === item
                ? 'border-b-2 border-brand-600 font-medium text-brand-700 dark:text-brand-300'
                : 'text-ink-500 hover:text-ink-800 dark:hover:text-ink-200')
            }
          >
            {item}
          </button>
        ))}
      </div>

      {tab === 'alerts' && (
        <>
          <div className="mt-4 flex items-center justify-between gap-3">
            <Select
              value={status}
              onChange={(e) => setStatus(e.target.value)}
              className="w-auto"
            >
              <option value="open">Still happening</option>
              <option value="resolved">Cleared</option>
              <option value="all">Everything</option>
            </Select>
            {!!summary?.unacknowledged && (
              <Button variant="secondary" onClick={() => ackAll.mutate()}>
                Acknowledge all ({summary.unacknowledged})
              </Button>
            )}
          </div>

          <Card className="mt-3" padded={false}>
            {alerts.isPending ? (
              <Loading />
            ) : alerts.error ? (
              <ErrorState error={alerts.error} retry={() => alerts.refetch()} />
            ) : !alerts.data.data.length ? (
              <EmptyState
                title={status === 'open' ? 'Nothing is wrong' : 'Nothing here'}
                description={
                  status === 'open'
                    ? 'No rule has found anything it was told to watch for.'
                    : 'Widen the filter to see more.'
                }
              />
            ) : (
              <ul className="divide-y divide-line">
                {alerts.data.data.map((alert) => (
                  <li key={alert.id} className="px-4 py-3">
                    <div className="flex items-start justify-between gap-4">
                      <div className="min-w-0">
                        <div className="flex flex-wrap items-center gap-2">
                          <Badge tone={TONE[alert.severity] ?? 'gray'}>
                            {alert.severity}
                          </Badge>
                          <span className="font-medium text-ink-900 dark:text-ink-50">
                            {alert.title}
                          </span>
                          {alert.resolved_at && <Badge tone="green">cleared</Badge>}
                          {alert.acknowledged_at && !alert.resolved_at && (
                            <Badge tone="gray">acknowledged</Badge>
                          )}
                        </div>
                        <div className="mt-1 text-xs text-ink-500">
                          {alert.rule_name} · {formatDateTime(alert.fired_at)}
                          {alert.resolved_at &&
                            ` · cleared ${formatDateTime(alert.resolved_at)}`}
                        </div>
                        <dl className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-xs text-ink-600 dark:text-ink-300">
                          {Object.entries(alert.detail).map(([key, value]) => (
                            <div key={key} className="flex gap-1">
                              <dt className="text-ink-400">{key.replace(/_/g, ' ')}</dt>
                              <dd className="font-mono">{String(value)}</dd>
                            </div>
                          ))}
                        </dl>
                        {alert.delivery.some((d) => !d.ok) && (
                          <p className="mt-2 text-xs text-red-600 dark:text-red-400">
                            Not delivered:{' '}
                            {alert.delivery
                              .filter((d) => !d.ok)
                              .map((d) => `${d.kind} — ${d.error}`)
                              .join('; ')}
                          </p>
                        )}
                      </div>
                      {!alert.acknowledged_at && (
                        <Button
                          variant="ghost"
                          onClick={() => ack.mutate(alert.id)}
                          className="shrink-0"
                        >
                          Acknowledge
                        </Button>
                      )}
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </Card>
        </>
      )}

      {tab === 'rules' && (
        <Card className="mt-4" padded={false}>
          {rules.isPending ? (
            <Loading />
          ) : !rules.data?.data.length ? (
            <EmptyState title="No rules" description="Nothing is being watched." />
          ) : (
            <ul className="divide-y divide-line">
              {rules.data.data.map((rule) => (
                <li key={rule.id} className="flex items-center gap-4 px-4 py-3">
                  <Toggle
                    label=""
                    checked={rule.enabled}
                    onChange={(v) => save.mutate({ id: rule.id, enabled: v })}
                  />
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="font-medium text-ink-900 dark:text-ink-50">
                        {rule.name}
                      </span>
                      <Badge tone={TONE[rule.severity] ?? 'gray'}>{rule.severity}</Badge>
                      {!!rule.open_alerts && (
                        <Badge tone="red">{rule.open_alerts} open</Badge>
                      )}
                      {rule.muted_until &&
                        new Date(rule.muted_until) > new Date() && (
                          <Badge tone="gray">
                            muted until {formatDateTime(rule.muted_until)}
                          </Badge>
                        )}
                    </div>
                    <div className="mt-0.5 text-xs text-ink-500">
                      {rule.label}
                      {rule.client_name ? ` · ${rule.client_name}` : ' · all clients'}
                      {rule.campaign ? ` · ${rule.campaign}` : ''} · at most once every{' '}
                      {every(rule.cooldown_sec)} ·{' '}
                      {rule.channels.length
                        ? rule.channels.map((c) => c.kind).join(', ')
                        : 'records only, sends nowhere'}{' '}
                      · {rule.fired_7d} in 7 days
                    </div>
                  </div>
                  <div className="flex shrink-0 gap-1">
                    <Button
                      variant="ghost"
                      onClick={() =>
                        save.mutate({
                          id: rule.id,
                          mute_minutes:
                            rule.muted_until &&
                            new Date(rule.muted_until) > new Date()
                              ? 0
                              : 60,
                        })
                      }
                    >
                      {rule.muted_until && new Date(rule.muted_until) > new Date()
                        ? 'Unmute'
                        : 'Mute 1h'}
                    </Button>
                    <Button variant="ghost" onClick={() => setEditing(rule)}>
                      Edit
                    </Button>
                    <Button
                      variant="ghost"
                      onClick={() => {
                        if (confirm(`Delete "${rule.name}"? Its history stays.`))
                          remove.mutate(rule.id);
                      }}
                    >
                      Delete
                    </Button>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </Card>
      )}

      {tab === 'delivery' && <Delivery />}

      {editing && kinds.data && (
        <RuleEditor
          rule={editing}
          specs={kinds.data.kinds}
          severities={kinds.data.severities}
          channelKinds={kinds.data.channels}
          saving={save.isPending}
          onCancel={() => setEditing(null)}
          onSave={(rule) => save.mutate(rule)}
        />
      )}
    </div>
  );
}

function RuleEditor({
  rule,
  specs,
  severities,
  channelKinds,
  saving,
  onCancel,
  onSave,
}: {
  rule: Partial<Rule>;
  specs: KindSpec[];
  severities: string[];
  channelKinds: string[];
  saving: boolean;
  onCancel: () => void;
  onSave: (rule: Partial<Rule>) => void;
}) {
  const [draft, setDraft] = useState<Partial<Rule>>(rule);
  const spec = specs.find((s) => s.kind === draft.kind) ?? specs[0];

  const set = (patch: Partial<Rule>) => setDraft((d) => ({ ...d, ...patch }));
  const setConfig = (key: string, value: unknown) =>
    setDraft((d) => ({ ...d, config: { ...(d.config ?? {}), [key]: value } }));

  return (
    <Modal
      open
      onClose={onCancel}
      title={rule.id ? 'Edit rule' : 'New rule'}
      footer={
        <div className="flex justify-end gap-2">
          <Button variant="secondary" onClick={onCancel}>
            Cancel
          </Button>
          <Button onClick={() => onSave(draft)} disabled={saving || !draft.name}>
            {saving ? 'Saving…' : 'Save'}
          </Button>
        </div>
      }
    >
      <div className="space-y-4">
        <Field label="Name">
          <Input
            value={draft.name ?? ''}
            placeholder="Calls stuck in progress"
            onChange={(e) => set({ name: e.target.value })}
          />
        </Field>

        {!rule.id && (
          <Field label="Watch for">
            <Select
              value={draft.kind ?? ''}
              onChange={(e) => {
                const next = specs.find((s) => s.kind === e.target.value);
                set({ kind: e.target.value, config: next?.default_config ?? {} });
              }}
            >
              {specs.map((s) => (
                <option key={s.kind} value={s.kind}>
                  {s.label}
                </option>
              ))}
            </Select>
          </Field>
        )}

        {spec && (
          <p className="rounded-md bg-sunken p-3 text-xs text-ink-600 dark:text-ink-300">
            {spec.blurb}
          </p>
        )}

        {spec?.fields.map((field) => (
          <Field key={field.key} label={field.label} hint={field.hint}>
            <Input
              type={field.type}
              min={field.min}
              max={field.max}
              step={field.step}
              value={String(
                (draft.config as Record<string, unknown>)?.[field.key] ??
                  field.default,
              )}
              onChange={(e) =>
                setConfig(
                  field.key,
                  field.type === 'number'
                    ? e.target.value === ''
                      ? ''
                      : Number(e.target.value)
                    : e.target.value,
                )
              }
            />
          </Field>
        ))}

        <div className="grid gap-3 sm:grid-cols-2">
          <Field label="Severity">
            <Select
              value={draft.severity ?? 'warning'}
              onChange={(e) => set({ severity: e.target.value })}
            >
              {severities.map((s) => (
                <option key={s} value={s}>
                  {s}
                </option>
              ))}
            </Select>
          </Field>
          <Field
            label="At most once every (minutes)"
            hint="How long this rule stays quiet after speaking. It keeps recording either way."
          >
            <Input
              type="number"
              min={1}
              max={10080}
              value={Math.round((draft.cooldown_sec ?? 3600) / 60)}
              onChange={(e) =>
                set({ cooldown_sec: Math.max(60, Number(e.target.value || 1) * 60) })
              }
            />
          </Field>
        </div>

        <Field
          label="Only this campaign"
          hint="Leave empty to watch every campaign."
        >
          <Input
            value={draft.campaign ?? ''}
            placeholder="ROOF1"
            onChange={(e) => set({ campaign: e.target.value })}
          />
        </Field>

        <div>
          <div className="flex items-center justify-between">
            <span className="text-sm font-medium text-ink-900 dark:text-ink-50">
              Send it to
            </span>
            <Select
              value=""
              className="w-auto"
              onChange={(e) => {
                if (!e.target.value) return;
                set({
                  channels: [...(draft.channels ?? []), { kind: e.target.value }],
                });
              }}
            >
              <option value="">Add a channel…</option>
              {channelKinds.map((k) => (
                <option key={k} value={k}>
                  {k}
                </option>
              ))}
            </Select>
          </div>
          <p className="mt-1 text-xs text-ink-500">
            A rule with no channels still records everything it finds. The history
            is worth having on its own.
          </p>
          <div className="mt-2 space-y-2">
            {(draft.channels ?? []).map((channel, index) => (
              <div key={index} className="flex items-center gap-2">
                <Badge tone="gray">{channel.kind}</Badge>
                <Input
                  className="flex-1"
                  placeholder={
                    channel.kind === 'email'
                      ? 'ops@example.com (empty uses the default)'
                      : channel.kind === 'telegram'
                        ? 'chat id (empty uses the default)'
                        : 'https://…'
                  }
                  value={
                    channel.kind === 'email'
                      ? (channel.to ?? '')
                      : channel.kind === 'telegram'
                        ? (channel.chat_id ?? '')
                        : (channel.url ?? '')
                  }
                  onChange={(e) => {
                    const key =
                      channel.kind === 'email'
                        ? 'to'
                        : channel.kind === 'telegram'
                          ? 'chat_id'
                          : 'url';
                    const next = [...(draft.channels ?? [])];
                    next[index] = { ...channel, [key]: e.target.value };
                    set({ channels: next });
                  }}
                />
                <Button
                  variant="ghost"
                  onClick={() =>
                    set({
                      channels: (draft.channels ?? []).filter((_, i) => i !== index),
                    })
                  }
                >
                  Remove
                </Button>
              </div>
            ))}
          </div>
        </div>
      </div>
    </Modal>
  );
}

function Delivery() {
  const qc = useQueryClient();
  const [draft, setDraft] = useState<Record<string, unknown> | null>(null);
  const [testTo, setTestTo] = useState('');

  const settings = useQuery({
    queryKey: ['alert-settings'],
    queryFn: () => api.get<Record<string, unknown>>('/admin/alerting/settings'),
  });

  const save = useMutation({
    mutationFn: (body: Record<string, unknown>) =>
      api.put('/admin/alerting/settings', body),
    onSuccess: () => {
      setDraft(null);
      qc.invalidateQueries({ queryKey: ['alert-settings'] });
    },
  });

  const test = useMutation({
    mutationFn: (channels: Channel[]) =>
      api.post<{ results: Array<{ kind: string; ok: boolean; error?: string }> }>(
        '/admin/alerting/settings/test',
        { channels },
      ),
  });

  if (settings.isPending) return <Loading />;
  const value = { ...(settings.data ?? {}), ...(draft ?? {}) } as Record<string, any>;
  const set = (patch: Record<string, unknown>) =>
    setDraft((d) => ({ ...(d ?? {}), ...patch }));

  return (
    <div className="mt-4 space-y-4">
      <Card
        title="Email"
        description="Configured once, under Notifications, and used by everything the platform sends."
      >
        <p className="text-sm text-ink-600 dark:text-ink-300">
          The mail server moved to <strong>Notifications → Email settings</strong>.
          There is one of them, shared by alerts and business notifications
          alike, and having it here made it look like alert-only configuration.
        </p>
        <div className="mt-3">
          <Link
            to="/notifications"
            className="text-sm font-medium text-brand-600 hover:underline dark:text-brand-400"
          >
            Open email settings →
          </Link>
        </div>
      </Card>

      <Card title="Telegram" description="A bot token from @BotFather.">
        <div className="grid gap-3 sm:grid-cols-2">
          <Field
            label="Bot token"
            hint={
              value.telegram_bot_token_set
                ? 'Already set. Leave empty to keep it.'
                : 'Encrypted before it is stored, and never sent back.'
            }
          >
            <Input
              type="password"
              placeholder={value.telegram_bot_token_set ? '••••••••' : ''}
              onChange={(e) => set({ telegram_bot_token: e.target.value })}
            />
          </Field>
          <Field label="Default chat id">
            <Input
              value={value.telegram_default_chat_id ?? ''}
              onChange={(e) => set({ telegram_default_chat_id: e.target.value })}
            />
          </Field>
        </div>
      </Card>

      <Card
        title="Send a test"
        description="Because “did I type the password right” should not be answered by an incident three days later."
      >
        <div className="flex flex-wrap items-end gap-2">
          <div className="flex-1">
            <Field label="To">
            <Input
              value={testTo}
              placeholder="ops@example.com"
              onChange={(e) => setTestTo(e.target.value)}
            />
            </Field>
          </div>
          <Button
            variant="secondary"
            disabled={test.isPending}
            onClick={() => test.mutate([{ kind: 'email', to: testTo }])}
          >
            Test email
          </Button>
          <Button
            variant="secondary"
            disabled={test.isPending}
            onClick={() => test.mutate([{ kind: 'telegram' }])}
          >
            Test Telegram
          </Button>
        </div>
        {test.data && (
          <ul className="mt-3 space-y-1 text-sm">
            {test.data.results.map((result, index) => (
              <li key={index} className={result.ok ? 'text-green-600' : 'text-red-600'}>
                {result.kind}: {result.ok ? 'delivered' : result.error}
              </li>
            ))}
          </ul>
        )}
      </Card>

      {draft && (
        <div className="flex justify-end gap-2">
          <Button variant="secondary" onClick={() => setDraft(null)}>
            Discard
          </Button>
          <Button onClick={() => save.mutate(draft)} disabled={save.isPending}>
            {save.isPending ? 'Saving…' : 'Save'}
          </Button>
        </div>
      )}
    </div>
  );
}
